import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backupApi } from "../api/backup";

const backupKeys = {
  all: ["backup"] as const,
  clusters: () => [...backupKeys.all, "clusters"] as const,
  clusterDetail: (ns: string, name: string) => [...backupKeys.all, "clusterDetail", ns, name] as const,
  clusterBackups: (ns: string, name: string) => [...backupKeys.all, "clusterBackups", ns, name] as const,
  operations: (filters?: Record<string, string | number | undefined>) => [...backupKeys.all, "operations", filters] as const,
  operation: (id: string) => [...backupKeys.all, "operation", id] as const,
};

export function useBackupClusters() {
  return useQuery({
    queryKey: backupKeys.clusters(),
    queryFn: () => backupApi.listClusters(),
    refetchInterval: 30_000,
  });
}

export function useClusterDetail(ns: string, name: string) {
  return useQuery({
    queryKey: backupKeys.clusterDetail(ns, name),
    queryFn: () => backupApi.getClusterDetail(ns, name),
    enabled: !!ns && !!name,
    refetchInterval: 30_000,
  });
}

export function useClusterBackups(ns: string, name: string) {
  return useQuery({
    queryKey: backupKeys.clusterBackups(ns, name),
    queryFn: () => backupApi.listBackups(ns, name),
    enabled: !!ns && !!name,
  });
}

export function useBackupOperations(filters?: { type?: string; status?: string; cluster?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: backupKeys.operations(filters),
    queryFn: () => backupApi.listOperations(filters),
    refetchInterval: 15_000,
  });
}

export function useBackupOperation(id: string) {
  return useQuery({
    queryKey: backupKeys.operation(id),
    queryFn: () => backupApi.getOperation(id),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed" || status === "cancelled") return false;
      return 5_000;
    },
  });
}

export function useTriggerBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ns, name }: { ns: string; name: string }) =>
      backupApi.triggerBackup(ns, name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: backupKeys.clusters() });
      qc.invalidateQueries({ queryKey: backupKeys.operations() });
    },
  });
}

export function useTriggerRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: backupApi.triggerRestore,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: backupKeys.operations() });
    },
  });
}

export function useTriggerProdToDev() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: backupApi.triggerProdToDev,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: backupKeys.operations() });
    },
  });
}

export function useCancelOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupApi.cancelOperation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: backupKeys.operations() });
    },
  });
}
